import { useState } from 'react';
import { Bell, X, Check, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { createSavedSearch } from '@/utils/savedSearches';
import { trackEvent } from '@/utils/marketplaceEvents';

/**
 * Modal: "Zapisz wyszukiwanie" — z opcjonalnym mailem do notyfikacji.
 *
 * Props:
 *  - open, onClose
 *  - criteria (current filters from URL): {type, rasa, miasto, wojewodztwo, rodowod, status}
 *  - onSaved(savedSearch) — callback po sukcesie
 */
export default function SaveSearchModal({ open, onClose, criteria, onSaved }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [digestMode, setDigestMode] = useState('instant'); // 'instant' | 'daily'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (!open) return null;

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...criteria,
        notify_email: email.trim() || null,
        notify_enabled: !!email.trim(),
        digest_mode: digestMode,
        name: name.trim() || null,
      };
      const res = await createSavedSearch(payload);
      trackEvent('save_search', { type: criteria.type, rasa: criteria.rasa, has_email: !!email.trim(), digest_mode: digestMode });
      setResult(res);
      onSaved?.(res.search);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Nie udało się zapisać. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  const summary = [
    criteria.type && criteria.type !== 'all' ? criteria.type.replace('mioty', 'szczenięta') : null,
    criteria.rasa,
    criteria.miasto || criteria.wojewodztwo,
  ].filter(Boolean).join(' · ') || 'wszystkie oferty';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="save-search-modal-overlay"
    >
      <div
        className="bg-[#0e1628] border border-white/10 rounded-2xl w-full max-w-md p-6 md:p-7 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="save-search-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-white"
          aria-label="Zamknij"
          data-testid="save-search-close"
        >
          <X className="w-5 h-5" />
        </button>

        {!result ? (
          <>
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-orange-500/20 border border-orange-500/40 mb-4">
              <Bell className="w-5 h-5 text-orange-300" />
            </div>
            <h3 className="text-xl font-bold text-white mb-1">Obserwuj to wyszukiwanie</h3>
            <p className="text-sm text-gray-400 mb-5">
              Damy znać, gdy pojawią się nowe ogłoszenia pasujące do Ciebie.
            </p>

            <div className="rounded-xl bg-white/5 border border-white/10 p-3 mb-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-1">Twoje kryteria</div>
              <div className="text-sm text-white">{summary}</div>
            </div>

            <label className="block text-sm text-gray-300 mb-1">Nazwa (opcjonalnie)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="np. Yorki w Warszawie"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/30 mb-4"
              data-testid="save-search-name"
            />

            <label className="block text-sm text-gray-300 mb-1">
              <Mail className="w-3.5 h-3.5 inline mr-1" />
              E-mail do powiadomień (opcjonalnie)
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ty@example.com"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/30 mb-2"
              data-testid="save-search-email"
            />
            <p className="text-xs text-gray-500 mb-4">
              Bez maila zapiszemy wyszukiwanie tylko lokalnie (zobaczysz w `/moje/wyszukiwania`).
            </p>

            {email.trim() && (
              <div className="mb-5" data-testid="save-search-digest-mode">
                <label className="block text-sm text-gray-300 mb-2">Tryb powiadomień</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setDigestMode('instant')}
                    className={`rounded-xl border px-3 py-2.5 text-left transition ${
                      digestMode === 'instant'
                        ? 'bg-orange-500/15 border-orange-400/60 text-white'
                        : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                    }`}
                    data-testid="save-search-digest-instant"
                  >
                    <div className="text-sm font-semibold flex items-center gap-1.5">⚡ Natychmiast</div>
                    <div className="text-[11px] text-gray-400 mt-0.5">Mail zaraz po dodaniu</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDigestMode('daily')}
                    className={`rounded-xl border px-3 py-2.5 text-left transition ${
                      digestMode === 'daily'
                        ? 'bg-emerald-500/15 border-emerald-400/60 text-white'
                        : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                    }`}
                    data-testid="save-search-digest-daily"
                  >
                    <div className="text-sm font-semibold flex items-center gap-1.5">📬 Dzienny digest</div>
                    <div className="text-[11px] text-gray-400 mt-0.5">Top oferty raz dziennie</div>
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm p-3 mb-4" data-testid="save-search-error">
                {error}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1 rounded-xl border-white/15 bg-transparent text-white hover:bg-white/5"
                data-testid="save-search-cancel"
              >
                Anuluj
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 rounded-xl bg-white text-black hover:bg-white/90 font-semibold"
                data-testid="save-search-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Zapisz'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-500/40 mb-4">
              <Check className="w-6 h-6 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-1">🔔 Zapisaliśmy</h3>
            <p className="text-sm text-gray-400 mb-4">
              {result.search.match_count_at_creation > 0
                ? `Powiadomimy Cię, gdy pojawią się nowe szczenięta pasujące do Twoich kryteriów. Aktualnie znaleźliśmy ${result.search.match_count_at_creation}.`
                : 'Powiadomimy Cię, gdy pojawią się nowe szczenięta pasujące do Twoich kryteriów.'}
            </p>

            {result.preview_matches?.length > 0 && (
              <div className="rounded-xl bg-white/5 border border-white/10 p-3 mb-4 max-h-60 overflow-y-auto">
                <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-2">Top dopasowania</div>
                {result.preview_matches.slice(0, 5).map((m, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 text-sm border-b border-white/5 last:border-0">
                    <div>
                      <div className="text-white truncate">{m.title}</div>
                      <div className="text-xs text-gray-500">{m.city || 'Polska'}</div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ml-2 ${
                      m.match_class.tier === 'perfect'
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                        : 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                    }`}>
                      {m.match_class.emoji} {Math.round(m.match_score * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            )}

            <Button
              onClick={onClose}
              className="w-full rounded-xl bg-white text-black hover:bg-white/90 font-semibold"
              data-testid="save-search-done"
            >
              Świetnie
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
